/**
 * Contextual Suggestions — rule-based suggestion engine.
 *
 * Reads workspace state and returns relevant action suggestions.
 * No LLM involved — purely deterministic rules.
 *
 * @module gibbs/contextualSuggestions
 */

const HIT_TO_LEAD_SUGGESTION = Object.freeze({
    text: 'Hit-to-lead',
    // The chip submits its text through Gibbs' campaign-intent router; this is
    // a workflow shortcut, not a single registry action.
    action: null,
});

function withHitToLead(suggestions) {
    return [HIT_TO_LEAD_SUGGESTION, ...suggestions];
}

const SUGGESTION_RULES = [
    {
        condition: (ws) => !ws.firstCaseData?.(),
        suggestions: [
            {
                text: 'Load a protein to get started',
                action: 'load-protein-from-pdb',
            },
            {
                text: 'Predict a protein structure with AI',
                action: 'fold-protein',
            },
        ],
    },
    {
        condition: (ws) => (
            ws.firstCaseData?.()
            && (!ws.getCompounds
                || ws.getCompounds().length === 0)
        ),
        suggestions: [
            {
                text: 'Load starter compounds',
                action: 'load-starter-compounds',
            },
            {
                text: 'Import a compound',
                action: 'load-compound',
            },
            {
                text: 'Search for fragments',
                action: 'grow-from-atom',
            },
        ],
    },
    {
        condition: (ws) => (
            // Correct API is getLoadedCompounds(); getCompounds does
            // not exist and was silently returning undefined so this
            // suggestion never fired.
            ws.getLoadedCompounds?.()?.length > 0
            && !ws.getActiveCompound?.()?.energies
        ),
        suggestions: [
            {
                text: 'Calculate binding energies',
                action: 'get-energies',
            },
            {
                text: 'Dock the compound',
                action: 'dock-compound',
            },
            {
                text: 'Energy minimize',
                action: 'energy-minimize',
            },
        ],
    },
    {
        condition: (ws) => ws.getActiveCompound?.()?.energies,
        suggestions: [
            {
                text: 'Predict ADMET properties',
                action: 'predict-admet',
            },
            {
                text: 'Score selectivity against another target',
                action: 'score-compounds',
            },
            {
                text: 'Export compounds',
                action: 'export-compounds',
            },
            {
                text: 'Generate a summary report',
                action: null,
            },
        ],
    },
];

// ── Domain-specific suggestions (Group 7.3) ──────────────────

const DOMAIN_SUGGESTIONS = {
    discovery: [
        { text: 'Dock the compound', action: 'dock-compound' },
        { text: 'Run energy minimization', action: 'energy-minimize' },
        { text: 'Predict ADMET properties', action: 'predict-admet' },
    ],
    clinical: [
        { text: 'Search for clinical trials', action: null },
        { text: 'Check dose-response data', action: null },
        { text: 'Review safety profile', action: null },
    ],
    corporate: [
        { text: 'Analyze recent earnings', action: null },
        { text: 'Review press releases', action: null },
        { text: 'Competitive intelligence', action: null },
    ],
};

/**
 * Get contextual suggestions based on workspace state.
 *
 * @param {Object} workspace - App.Workspace or equivalent
 * @param {string} [domain] - Active domain for domain-specific chips
 * @returns {Array<{text: string, action: string|null}>}
 */
export function getSuggestions(workspace, domain) {
    // If a non-discovery domain is active, return domain chips
    if (domain && domain !== 'discovery' && DOMAIN_SUGGESTIONS[domain]) {
        return DOMAIN_SUGGESTIONS[domain];
    }

    if (!workspace) {
        return withHitToLead(DOMAIN_SUGGESTIONS.discovery);
    }
    for (const rule of SUGGESTION_RULES) {
        try {
            if (rule.condition(workspace)) {
                return withHitToLead(rule.suggestions);
            }
        } catch (_) { continue; }
    }
    return withHitToLead(DOMAIN_SUGGESTIONS.discovery);
}

/**
 * Get domain-specific suggestions by domain key.
 *
 * @param {string} domain - 'discovery' | 'clinical' | 'corporate'
 * @returns {Array<{text: string, action: string|null}>}
 */
export function getDomainSuggestions(domain) {
    if (domain && domain !== 'discovery' && DOMAIN_SUGGESTIONS[domain]) {
        return DOMAIN_SUGGESTIONS[domain];
    }
    return withHitToLead(DOMAIN_SUGGESTIONS.discovery);
}
